import { useEffect } from 'react';
import {
  useRequestFormStore,
  openFreshCreateRequestSession,
} from 'src/features/requests/request-form';
import { WebViewAuthService } from 'src/services/Auth/WebView.auth';
import {
  getIsCreateRequestFromWebviewConfig,
  getBrandIdFromWebviewConfig,
} from 'src/utils/webview-detection';
import { useVideoVolumeModalStore } from 'src/features/company/use-video-volume-modal-store';
import { useShallow } from 'zustand/react/shallow';
import * as brandClient from 'src/lib/services/api/brand-api/brand-client';

/**
 * Component that automatically opens the RequestWizard when the app
 * is loaded from a ReactNative WebView with specific URL parameters
 */
export const WebViewWizardInitializer = () => {
  const { discardModal, isModalOpen } = useRequestFormStore();
  const { setIsModalOpen: setVideoVolumeModalOpen, isModalOpen: isVideoVolumeModalOpen } =
    useVideoVolumeModalStore(
      useShallow(({ setIsModalOpen, isModalOpen }) => ({
        setIsModalOpen,
        isModalOpen,
      })),
    );

  const isCreateRequest = getIsCreateRequestFromWebviewConfig();
  const showWhiteOverlay = !!isCreateRequest && isVideoVolumeModalOpen && !isModalOpen;

  const handleOpenModal = async () => {
    // handleOpenModal re-runs on every 'visibilitychange' resume (e.g.
    // backgrounding the webview to use the native camera/photo picker
    // mid-flow), not just the initial launch — and always has, independent
    // of this fix, so the config/video-volume re-validation below must keep
    // running on every resume as before.
    if (!getIsCreateRequestFromWebviewConfig()) {
      discardModal();
      return;
    }

    // Check if brand needs video volume question before opening request modal
    const brandId = getBrandIdFromWebviewConfig();
    if (brandId) {
      try {
        const fullBrandData = await brandClient.get(brandId);
        const monthlyVideoCount = fullBrandData.brand_metadata?.monthly_video_count;

        if (!monthlyVideoCount || monthlyVideoCount.trim() === '') {
          discardModal();
          setVideoVolumeModalOpen(true);
          return;
        }
      } catch (error) {
        discardModal();
        setVideoVolumeModalOpen(true);
        return;
      }
    }

    // Only the reset-and-(re)open below is guarded on session state: a
    // resume while the wizard is already open OR minimized means a session
    // is genuinely in progress, and resetting here would clear
    // 'formValues'/currentRequestId (guarding a genuinely fresh launch
    // against a stale draft, #2565) and snap currentStep back to 2 —
    // discarding that in-progress session on every single resume. Read live
    // from the store (not the closed-over destructured values) since this
    // function is bound once into the mount-only effect below and would
    // otherwise always see this render's initial `false` values.
    const { isModalOpen: isModalOpenNow, isMinimized: isMinimizedNow } =
      useRequestFormStore.getState();
    if (isModalOpenNow || isMinimizedNow) {
      return;
    }

    // This is a "create new request" launch that calls the store's openModal
    // directly, bypassing useOpenRequestModal's usual draft reset. A stale
    // 'formValues' draft from a previous session would otherwise seed this
    // new request's initial values (#2565).
    openFreshCreateRequestSession();
  };

  useEffect(() => {
    if (!WebViewAuthService.isWebViewAuthEnabled() || !getIsCreateRequestFromWebviewConfig()) {
      return;
    }
    // Don't send NAVIGATE if the video volume modal is showing instead
    const isVideoVolumeOpen = useVideoVolumeModalStore.getState().isModalOpen;
    if (!isModalOpen && !isVideoVolumeOpen) {
      window.ReactNativeWebView?.postMessage(
        JSON.stringify({
          type: 'NAVIGATE',
          secure: true,
          to: '/requests',
          params: {
            isCreate: !!getIsCreateRequestFromWebviewConfig(),
          },
        }),
      );
    }
  }, [isModalOpen]);

  useEffect(() => {
    if (!WebViewAuthService.isWebViewAuthEnabled()) return;
    handleOpenModal();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        handleOpenModal();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  if (!showWhiteOverlay) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'white',
        zIndex: 999,
      }}
    />
  );
};
